import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import { useT } from '../i18n/I18nProvider';
import { useCommands, type Command } from '../navigation/commands';
import { useResourceEntries, type PaletteEntry } from '../navigation/resource-search';
import { readRecent } from '../hooks/useRecent';

type T = (key: string) => string;

/** Substring ranking: exact > prefix > title-contains > keyword/breadcrumb-contains. */
function rank(commands: Command[], query: string, t: T): Command[] {
  const q = query.trim().toLowerCase();
  const scored = commands.map((c) => {
    const title = t(c.titleKey).toLowerCase();
    if (!q) return { c, score: 1 };
    const hay = [title, ...c.breadcrumbKeys.map((k) => t(k).toLowerCase()), ...(c.keywords ?? [])]
      .join(' ')
      .toLowerCase();
    let score = 0;
    if (title === q) score = 100;
    else if (title.startsWith(q)) score = 80;
    else if (title.includes(q)) score = 60;
    else if (hay.includes(q)) score = 40;
    return { c, score };
  });
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6)
    .map((s) => s.c);
}

/**
 * The Connaro command palette (⌘K / Ctrl+K).
 *
 * A fast, keyboard-first way to reach any *permitted* destination or resource without
 * browsing the sidebar. Navigation matches come from the permission-scoped registry;
 * resource matches (e.g. Edge Devices by name) come from useResourceEntries. Both are
 * permission-scoped, so the palette never offers — or names — something the user
 * cannot open.
 */
export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const t = useT();
  const commands = useCommands();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const toEntry = (c: Command): PaletteEntry => ({
    id: c.id,
    title: t(c.titleKey),
    context: c.breadcrumbKeys.map((k) => t(k)).join(' › '),
    route: c.route,
    icon: c.icon,
  });
  const navEntries = useMemo(() => rank(commands, query, t).map(toEntry), [commands, query, t]);
  const resourceEntries = useResourceEntries(query, open, t('nav.devices'));
  // With an empty query, offer recent destinations (resolved + permission-filtered
  // via `commands`) instead of an undifferentiated dump; fall back to top nav.
  const recentEntries = useMemo(() => {
    if (query.trim() || !open) return [];
    return readRecent()
      .map((r) => commands.find((c) => c.route === r))
      .filter((c): c is Command => Boolean(c))
      .slice(0, 6)
      .map(toEntry);
  }, [query, open, commands, t]);
  const entries = useMemo(() => {
    if (!query.trim()) return recentEntries.length > 0 ? recentEntries : navEntries;
    return [...navEntries, ...resourceEntries];
  }, [query, navEntries, resourceEntries, recentEntries]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => setActive(0), [query]);

  if (!open) return null;

  const go = (entry: PaletteEntry): void => {
    onClose();
    navigate(entry.route);
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, entries.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const entry = entries[active];
      if (entry) go(entry);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="cmdk"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cmdk__panel" role="dialog" aria-modal="true" aria-label={t('palette.title')}>
        <div className="cmdk__search">
          <Search size={18} aria-hidden="true" className="cmdk__search-icon" />
          <input
            ref={inputRef}
            className="cmdk__input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={entries[active] ? `cmdk-opt-${entries[active].id}` : undefined}
            aria-label={t('palette.placeholder')}
            placeholder={t('palette.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <ul className="cmdk__list" id="cmdk-list" role="listbox" aria-label={t('palette.title')}>
          {entries.length === 0 ? (
            <li className="cmdk__empty">{t('palette.empty')}</li>
          ) : (
            entries.map((entry, idx) => {
              const Icon = entry.icon;
              return (
                <li
                  key={entry.id}
                  id={`cmdk-opt-${entry.id}`}
                  role="option"
                  aria-selected={idx === active}
                  className={`cmdk__opt${idx === active ? ' active' : ''}`}
                  onMouseEnter={() => setActive(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    go(entry);
                  }}
                >
                  <Icon size={16} className="cmdk__opt-icon" aria-hidden="true" />
                  <span className="cmdk__opt-title">{entry.title}</span>
                  <span className="cmdk__opt-crumb">{entry.context}</span>
                  {idx === active && (
                    <CornerDownLeft size={14} className="cmdk__opt-enter" aria-hidden="true" />
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
